import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import '../insurance.scss';
import insuranceSquare from './../../assets/images/insurance-shapes-square.svg';
import insuranceTriangle from './../../assets/images/insurance-shapes-triangle.svg';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function InsuranceUsage() {

  const square1Ref = useRef();
  const square2Ref = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }
        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(square1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(square2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => { ctx.revert(); /* <- cleanup! */ };

  }, []);

  useEffect(() => {
    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="insurance-future">

        <p id="s1" className="intro" data-aos="fade-up">Discover how insurance businesses plan to use direct mail marketing in the future.&#8288;<a href="#reference-1" class="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Motivators for More Mail Use*</h3>

                <p className="pull-quote no-margin-bottom"><span data-aos className='highlighter'>Easier personalization</span></p>
                <p>Insurance</p>

                <ul className='no-icons'>
                  <li>Financial — Ability to track campaigns from print to delivery</li>
                  <li>Health — Easier audience targeting</li>
                  <li>Retail — Higher return on ad spend (ROAS)</li>
                </ul>

                <p className="disclaimer">*Consumer packaged goods companies had too low a sample size to be included in this part of the survey.</p>
                <p>More than companies in any other sector, insurance businesses reported that easier personalization capabilities would be the biggest motivator for increasing direct mail use going forward.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Trying to better capture millennials’ attention? <strong><a href="/../why-personalization-may-help-you-win-with-millennials/">Personalization may be the key</a></strong> to resonating with this selective demographic, who value meaningful connections, genuine brand values and customized offerings.</p>
              </div>
            </div>

            <div className='inner-cols'>
              <div className='inner-col-item'>
                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Impediments to Mail Use</h3>  
                    <div className='progress-bar-area'>
                      <div className='progressbar-item'>
                        <span className='label'>Cost per mailing/too expensive</span>
                        <div className='progress-bar size-small' data-aos data-aos-id="bar-anim">
                          <div className='progress' data-fill="35"><span class="percentage" data-to="35" aria-hidden="true">0</span><span class="screen-reader-text">35</span>%</div>
                        </div>
                      </div>
                    </div>
                    <p>Overall, 35% of businesses across all industries report that cost per mailing, and mail being too expensive generally, are the main impediments to using it. Insurance companies also report this as the main reason for not using mail more often.</p>
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                    <p id="s3">Mail can be a powerful marketing channel: Nearly seven out of 10 marketers feel it is the highest ROI marketing channel at their disposal.&#8288;<a href="#reference-3" class="cm-article-content__reference" aria-label="Footnote 3" title="Footnote 3"><sup aria-hidden="true">[3]</sup></a> To keep down upfront mailing costs, weed out any repeats on your mailing lists so you don’t waste money sending out multiple mailpieces to the same address—and don’t overwhelm your customers.</p>
                  </div>
                </div>
              </div>
              <div className='inner-col-item'>
                <div className='personalized-box secondary align-to-text'>
                  <h3>Keeping mailing lists up to date can bring down costs and increase ROI.</h3>
                  <div data-aos data-aos-id="vector-shapes" className='shape-images'>
                    <div className='triangle triangle-1'>
                      <img ref={triangle1Ref} src={insuranceTriangle} alt="" />
                    </div>
                    <div className='square square-1'>
                      <img ref={square1Ref} src={insuranceSquare} alt="" />
                    </div>
                    <div className='triangle triangle-2'>
                      <img ref={triangle2Ref} src={insuranceTriangle} alt="" />
                    </div>
                    <div className='square square-2'>
                      <img ref={square2Ref} src={insuranceSquare} alt="" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-right'>
            
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Using Direct Mail in the Future</h3>
                <div className='pie-chart' data-aos>
                  <svg viewBox="0 0 32 32" role="img" alt="" title="">
                    <circle r="16" cx="16" cy="16" strokeDasharray="0 100"></circle>
                  </svg>
                </div>

                <span data-aos data-aos-id="percentage" data-aos-anchor=".pie-chart">
                  <span data-aos className='percentage-value'><span class="percentage" data-to="93" aria-hidden="true">0</span><span class="screen-reader-text">93</span>%</span>
                </span>

                <p>More or the same</p>
                <p>Out of all the industries surveyed, insurance companies are the most likely to use direct mail the same amount or more in future campaigns.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p id="s2">Direct mail is ever-evolving, with more ways than ever to connect it to digital marketing for actionable omnichannel campaigns. Including a QR Code<sup>&reg;</sup>&#8288;<a href="#reference-2" class="cm-article-content__reference" aria-label="Footnote 2" title="Footnote 2"><sup aria-hidden="true">[2]</sup></a> or personalized URL on mailpieces, for example, makes it easier for customers to engage, while even advanced tech like augmented reality and virtual reality is becoming more accessible.</p>
              </div>
            </div> 
          </div>
        </div>
      </div>
    </>
  );
}
